import { ApplicationConfig, isDevMode, provideBrowserGlobalErrorListeners, provideAppInitializer, inject } from '@angular/core';
import { provideRouter, withPreloading, PreloadAllModules, TitleStrategy } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { DOCUMENT } from '@angular/common';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { modalReducer } from './core/store/modal.reducer';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { TranslationService } from './core/services/translation.service';
import { AppTitleStrategy } from './core/services/app-title-strategy';
import { ApiTokenService } from './core/services/api-token.service';
import { ProfileService } from './core/services/profile.service';
import { PermissionService } from './core/services/permission.service';
import { environment } from '@env/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(
      withInterceptors([authInterceptor, errorInterceptor]),
    ),
    provideStore({
      modal: modalReducer,
    }),
    ...(isDevMode() ? [provideStoreDevtools({ maxAge: 25 })] : []),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideAppInitializer(() => {
      const doc = inject(DOCUMENT);
      const key = environment.googleMapsApiKey;
      if (key) {
        const script = doc.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&loading=async`;
        script.async = true;
        script.defer = true;
        doc.head.appendChild(script);
      }
    }),
    provideAppInitializer(() => {
      const translationService = inject(TranslationService);
      return translationService.loadLanguage(translationService.activeLang());
    }),
    provideAppInitializer(async () => {
      if (environment.demoAuth) return;
      const tokenService  = inject(ApiTokenService);
      const profileService = inject(ProfileService);
      const permissionService = inject(PermissionService);
      if (!tokenService.getToken()) return;
      try {
        const profile = await firstValueFrom(profileService.getProfile());
        const fullName = `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim();
        if (fullName) tokenService.setUserName(fullName);
        if (profile.role) tokenService.setRole(profile.role);
        tokenService.setAvatar(profile.avatar ?? null);
        permissionService.setPermissions(profile.permissions ?? []);
      } catch {
        // 401 → errorInterceptor redirects to /login; other errors fail silently
      }
    }),
  ],
};